import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { PdfPageDetails } from "@/Common/Components/Pdf/PdfViewer/PdfViewer";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { InboundFaxesUIStateInbox, InboundFaxMailboxType } from "@/Common/Models/UI/Enums";
import { InboundFaxesMailbox } from "@/Documents/Components/InboundFax/InboundFaxesMailbox";
import { InboundFaxesMailboxActions } from "@/Documents/Components/InboundFax/InboundFaxesMailboxActions";
import { InboundFaxIndexingView } from "@/Documents/Components/InboundFax/InboundFaxIndexingView";
import { InboundFax } from "@/Documents/Models/InboundFax/InboundFax";
import { InboundFaxDetails } from "@/Documents/Models/InboundFax/InboundFaxDetails";
import { InboundFaxMailboxModel } from "@/Documents/Models/InboundFax/InboundFaxMailboxModel";
import { InboundFaxView } from "@/Documents/Models/UI/InboundFaxEnums";
import { OutboundFaxCreationPopup } from "../OutboundFax/OutboundFaxCreationPopup";
import { ReturnFaxToSenderPopup } from "./ReturnFaxToSenderPopup";

interface InboundFaxesViewProps {
    mailboxModel: InboundFaxMailboxModel;
    inboxType: InboundFaxesUIStateInbox;
    descriptionsAutoCompleteList?: Immutable.List<SearchableItemModelT<string>>;
    sourcesAutoCompleteList?: Immutable.List<SearchableItemModelT<string>>;
}

class InboundFaxesViewState {
    public selectedDocumentPages?: PdfPageDetails[];
    public showMailboxSettings: boolean;
    public showReturnToSenderPopup: boolean;
    public returnToSenderFaxes: InboundFax[];

    public static getDefaultState(): InboundFaxesViewState {
        const state = new InboundFaxesViewState();
        state.selectedDocumentPages = [];
        state.showMailboxSettings = false;
        state.showReturnToSenderPopup = false;
        state.returnToSenderFaxes = null;

        return state;
    }
}

class InboundFaxesView extends BaseReactComponent<InboundFaxesViewProps, InboundFaxesViewState> {
    public constructor(props: InboundFaxesViewProps) {
        super(props);
        this.state = InboundFaxesViewState.getDefaultState();
    }

    public componentDidUpdate(prevProps: InboundFaxesViewProps): void {
        const prevMailbox = prevProps.mailboxModel;
        const currentMailbox = this.props.mailboxModel;

        if (
            // control fax list reloading here.
            this.props.inboxType !== prevProps.inboxType
            || currentMailbox.queryOrderDirection !== prevMailbox.queryOrderDirection
            || currentMailbox.queryOrderType !== prevMailbox.queryOrderType
            || currentMailbox.searchText !== prevMailbox.searchText
            || currentMailbox.queryCurrentPageNumber !== prevMailbox.queryCurrentPageNumber
            || currentMailbox.pdfViewerSelectedZoomLevelIndex !== prevMailbox.pdfViewerSelectedZoomLevelIndex
            || Object.keys(currentMailbox.filteringSettings).any(key => currentMailbox.filteringSettings[key] !== prevMailbox.filteringSettings[key])
        )
            this.refreshFaxList();

        if (this.props.mailboxModel.currentView !== prevProps.mailboxModel.currentView) {
            $(window).trigger("resize"); // this fits the components into available screen space when switching views between the faxes list and indexing.
        }
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
        this.refreshFaxList();
    }

    public render(): JSX.Element {
        return (
            <div className="workspace">
                {this.state.showReturnToSenderPopup && (
                    <FeatureFlagControl
                        flag={cacheStore.ClinicSettings.EnableFaxCoverSheet}
                        activeFeature={(
                            <OutboundFaxCreationPopup
                                onCancel={() => this.mutateState(s => s.showReturnToSenderPopup = false)}
                                onSubmit={(faxDetails, onCompleted) => this.returnToSender(this.state.returnToSenderFaxes, faxDetails, onCompleted)}
                                popupTitle={cacheStore.GetString("ReturnToSenderTitleLabel")}
                                initialFaxNumber={this.state.returnToSenderFaxes.firstOrDefault()?.SenderFaxNumber}
                                selectedDocumentPages={this.state.selectedDocumentPages}
                                showX
                                returnFaxSender
                            />
                        )}
                        fallbackFeature={(
                            <ReturnFaxToSenderPopup
                                title={cacheStore.GetString("ReturnToSenderTitleLabel")}
                                onAccept={faxDetails => this.returnToSender(this.state.returnToSenderFaxes, faxDetails)}
                                onCancel={() => this.mutateState(s => s.showReturnToSenderPopup = false)}
                                initialFaxNumber={this.state.returnToSenderFaxes.firstOrDefault()?.SenderFaxNumber}
                                clickableTextContext="ReturnToSenderNotes"
                            />
                        )}
                    />
                )}
                <CommonTableHeaderComponent
                    title={this.getMailboxTitle()}
                    searchText={this.props.mailboxModel.searchText}
                    searchPlaceholder={cacheStore.GetString("SearchInFaxesLabel")}
                    onSearch={text => InboundFaxesMailboxActions.onSearchTextChange(text)}
                    onRefreshClick={() => this.refreshFaxList()}
                    onSettingsClick={() => this.mutateState(s => s.showMailboxSettings = !s.showMailboxSettings)}
                    hideRightSideComponents={this.HasToHideRightSideComponent()}
                />
                {this.props.mailboxModel.currentView == InboundFaxView.FaxesList && (
                    <InboundFaxesMailbox
                        mailboxModel={this.props.mailboxModel}
                        inboxType={this.props.inboxType}
                        onFaxClick={(dataSourceItem: InboundFax) => this.handleOnFaxClick(dataSourceItem)}
                        onRefreshFaxList={() => this.refreshFaxList()}
                        onShowReturnFaxToSenderPopup={faxes => this.showReturnFaxToSenderPopup(faxes)}
                        mailboxSettingsAreVisible={this.state.showMailboxSettings}
                        onCloseMailboxSettings={() => this.mutateState(s => s.showMailboxSettings = false)}
                    />
                )}
                {(
                    this.props.mailboxModel.currentView === InboundFaxView.IndexingForm ||
                    this.props.mailboxModel.currentView === InboundFaxView.TriageForm
                ) && (
                    <InboundFaxIndexingView
                        onNextFax={() => this.onNextFax()}
                        onPreviousFax={() => this.onPreviousFax()}
                        fax={this.props.mailboxModel.currentFax}
                        onCancel={() => this.closeIndexingForm()}
                        currentFaxIndex={this.getOverallFaxIndex()}
                        totalFaxCount={this.props.mailboxModel.queryRowCount}
                        showReturnFaxToSenderPopup={(selectedPages?: PdfPageDetails[]) => this.showReturnFaxToSenderPopup([this.props.mailboxModel.currentFax], selectedPages)}
                        onAfterAttachPages={(currentFax: InboundFax) => this.onAfterAttachPages(currentFax)}
                        pdfViewerSelectedZoomLevelIndex={this.props.mailboxModel.pdfViewerSelectedZoomLevelIndex}
                        onRefreshAndGoNext={() => this.onRefreshAndGoNext()}
                        onUpdateFaxesClassAndSubClass={(
                            senderFaxNumber: string,
                            classId: string,
                            subClassId: string,
                            hasAssignedClassToFaxSource: boolean
                        ) => this.onUpdateFaxesClassAndSubClass(
                            senderFaxNumber,
                            classId,
                            subClassId,
                            hasAssignedClassToFaxSource
                        )}
                        mailboxModel={this.props.mailboxModel}
                        onUpdatePendingPage={(faxId: string) => InboundFaxesMailboxActions.subtractOnePendingPage(this.props.mailboxModel.faxes, faxId)}
                        descriptionsAutoCompleteList={this.props.descriptionsAutoCompleteList}
                        sourcesAutoCompleteList={this.props.sourcesAutoCompleteList}
                        onRemoveFaxSourcesClass={senderFaxNumber => this.onRemoveFaxSourcesClass(senderFaxNumber)}
                    />
                )
                }
            </div>
        );
    }

    private getMailboxTitle(): string {
        switch (this.props.mailboxModel.currentView) {
            case InboundFaxView.IndexingForm:
                return cacheStore.GetString("FaxesLabel");
            case InboundFaxView.TriageForm:
                return cacheStore.GetString("DocumentTriageLabel");
        }

        switch (this.props.inboxType) {
            case InboundFaxesUIStateInbox.Inbox:
                return cacheStore.GetString("ReceivedFaxesLabel");
            case InboundFaxesUIStateInbox.Trash:
                return cacheStore.GetString("TrashLabel");
            case InboundFaxesUIStateInbox.Indexed:
                return cacheStore.GetString("IndexedLabel");
            default:
                return "";
        }
    }

    private async refreshFaxList(): Promise<void> {
        await InboundFaxesMailboxActions.refreshFaxesList(
            this.props.mailboxModel,
            this.getMailboxType()
        );
    }

    private async handleOnFaxClick(fax: InboundFax): Promise<void> {
        if (this.props.inboxType === InboundFaxesUIStateInbox.Inbox) {
            InboundFaxesMailboxActions.showFaxIndexingForm(fax);
        }
        else if (this.props.inboxType === InboundFaxesUIStateInbox.Trash) {
            await InboundFaxesMailboxActions.GeneratePdfPreview(fax.Id, [0], InboundFaxMailboxType.Trash);
        }
        else if (this.props.inboxType === InboundFaxesUIStateInbox.Indexed) {
            await InboundFaxesMailboxActions.GeneratePdfPreview(fax.Id, [0], InboundFaxMailboxType.Indexed);
        }

        await InboundFaxesMailboxActions.ensureFaxIsMarkedAsRead(fax, this.props.mailboxModel.faxes);
    }

    private closeIndexingForm(): void {
        InboundFaxesMailboxActions.showFaxMailbox();
    }

    private async onNextFax(): Promise<void> {
        if (this.getIsCurrentFaxVeryLastFax()) {
            InboundFaxesMailboxActions.showFaxMailbox();
            return;
        }

        if (this.getIsCurrentFaxLastFaxOnCurrentPage()) {
            const nextFax = await InboundFaxesMailboxActions
                .loadNextPageAndReturnFirstFax(
                    this.props.mailboxModel,
                    this.getMailboxType()
                );

            if (nextFax !== null) {
                this.setFaxAsCurrentAndMarkAsRead(nextFax);
            }

            return;
        }

        const nextFax = this.props.mailboxModel.faxes[this.getCurrentFaxIndex() + 1];
        this.setFaxAsCurrentAndMarkAsRead(nextFax);
    }

    private async onPreviousFax(): Promise<void> {
        if (this.getIsCurrentFaxVeryFirstFax())
            return;

        if (this.getIsCurrentFaxFirstFaxOnCurrentPage()) {
            const previousFax = await InboundFaxesMailboxActions
                .loadPreviousPageAndReturnLastFax(
                    this.props.mailboxModel,
                    this.getMailboxType()
                );

            if (previousFax !== null) {
                this.setFaxAsCurrentAndMarkAsRead(previousFax);
            }

            return;
        }

        const previousFax = this.props.mailboxModel.faxes[this.getCurrentFaxIndex() - 1];
        this.setFaxAsCurrentAndMarkAsRead(previousFax);
    }

    private getCurrentFaxIndex(): number {
        return this.props.mailboxModel.faxes.findIndex(tableItem => tableItem.Id === this.props.mailboxModel.currentFax?.Id);
    }

    private getIsCurrentFaxVeryFirstFax(): boolean {
        return this.props.mailboxModel.queryCurrentPageNumber === 1
            && this.getCurrentFaxIndex() === 0;
    }

    private getIsCurrentFaxVeryLastFax(): boolean {
        return this.props.mailboxModel.queryCurrentPageNumber === this.props.mailboxModel.queryPageCount
            && this.getCurrentFaxIndex() === this.props.mailboxModel.faxes.length - 1;
    }

    private getIsCurrentFaxFirstFaxOnCurrentPage(): boolean {
        return this.getCurrentFaxIndex() === 0;
    }

    private getIsCurrentFaxLastFaxOnCurrentPage(): boolean {
        return this.getCurrentFaxIndex() === this.props.mailboxModel.filteringSettings.QueryPageSize - 1;
    }

    private showReturnFaxToSenderPopup(faxesToReturn: InboundFax[], selectedPages?: PdfPageDetails[]): void {
        this.mutateState(s => {
            s.showReturnToSenderPopup = true;
            s.returnToSenderFaxes = faxesToReturn;
            s.selectedDocumentPages = selectedPages;
        });
    }

    private async returnToSender(faxes: InboundFax[], faxDetails: InboundFaxDetails, onCompleted?: VoidFunction): Promise<void> {
        const faxIds = faxes.map(fax => fax.Id);
        const documentPages = cacheStore.ClinicSettings.EnableFaxCoverSheet && this.state.selectedDocumentPages?.length > 0 ? this.state.selectedDocumentPages.map(document => document.pageNumber) : undefined;
        const response = await InboundFaxesMailboxActions.returnToSender(faxIds, faxDetails, documentPages);

        if (response.Warnings?.any()) {
            sessionStore.showPopup({
                title: cacheStore.GetString("WarningLabel"),
                content: response.Warnings.firstOrDefault(),
                okAction: () => onCompleted?.()
            });
        }
        else {
            this.mutateState(
                s => {
                    s.showReturnToSenderPopup = false;
                    s.returnToSenderFaxes = null;
                    s.selectedDocumentPages = [];
                },
                async () => {
                    if (this.props.mailboxModel.currentView === InboundFaxView.FaxesList) {
                        await this.refreshFaxList();
                    }
                    else {
                        await this.onRefreshAndGoNext();
                    }
                }
            );
            await InboundFaxesMailboxActions.updateInboundFaxUnmanagedFaxCount(documentsStore.InboundFaxInboxModel.filteringSettings);
        }
    }

    private async onAfterAttachPages(updatedCurrentFax: InboundFax): Promise<void> {
        if (updatedCurrentFax.PendingPageCount === 0) {
            await this.onRefreshAndGoNext();
        }
        else {
            InboundFaxesMailboxActions.setCurrentFax(updatedCurrentFax, this.props.mailboxModel.faxes);
        }
    }

    private getOverallFaxIndex(): number {
        return this.props.mailboxModel.filteringSettings.QueryPageSize * (this.props.mailboxModel.queryCurrentPageNumber - 1) + this.getCurrentFaxIndex();
    }

    private getMailboxType(): InboundFaxMailboxType {
        switch (this.props.inboxType) {
            case InboundFaxesUIStateInbox.Inbox:
                return InboundFaxMailboxType.Inbox;
            case InboundFaxesUIStateInbox.Trash:
                return InboundFaxMailboxType.Trash;
            case InboundFaxesUIStateInbox.Indexed:
                return InboundFaxMailboxType.Indexed;
            default:
                return -1;
        }
    }

    private async onRefreshAndGoNext(): Promise<void> {
        await this.onNextFax();
        await this.refreshFaxList();
    }

    private setFaxAsCurrentAndMarkAsRead(fax: InboundFax): void {
        InboundFaxesMailboxActions.setCurrentFax(fax, this.props.mailboxModel.faxes);
        InboundFaxesMailboxActions.ensureFaxIsMarkedAsRead(fax, this.props.mailboxModel.faxes);
    }

    private onUpdateFaxesClassAndSubClass(senderFaxNumber: string, classId: string, subClassId: string, hasAssignedClassToFaxSource: boolean): void {
        const updatedFaxes = this.props.mailboxModel.faxes.map(fax => {
            const updatedFax = { ...fax };

            if (fax.SenderFaxNumber === senderFaxNumber) {
                updatedFax.ReportClassLookupId = classId;
                updatedFax.ReportSubClassLookupId = subClassId;

                if (hasAssignedClassToFaxSource) {
                    updatedFax.FaxSourceClassLookupId = classId;
                    updatedFax.FaxSourceSubClassLookupId = subClassId;
                }
            }

            return updatedFax;
        });
        InboundFaxesMailboxActions.updateFaxes(updatedFaxes);
    }

    private HasToHideRightSideComponent(): boolean {
        return this.props.mailboxModel.currentView === InboundFaxView.IndexingForm ||
            this.props.mailboxModel.currentView === InboundFaxView.TriageForm;
    }

    private onRemoveFaxSourcesClass(senderFaxNumber: string): void {
        const updatedFaxes = this.props.mailboxModel.faxes.map(fax => {
            const updatedFax = { ...fax };

            if (fax.SenderFaxNumber === senderFaxNumber) {
                updatedFax.FaxSourceClassLookupId = "";
                updatedFax.FaxSourceSubClassLookupId = "";
            }

            return updatedFax;
        });
        InboundFaxesMailboxActions.updateFaxes(updatedFaxes);

        const currentFax = new InboundFax({ ...this.props.mailboxModel.currentFax });
        if (currentFax.SenderFaxNumber === senderFaxNumber) {
            currentFax.FaxSourceClassLookupId = "";
            currentFax.FaxSourceSubClassLookupId = "";
            InboundFaxesMailboxActions.updateCurrentFax(currentFax);
        }
    }
}

export { InboundFaxesView };
